import { COLLECTOR_RULES } from "../../config/gameplay";
import { overdriveLaneMultipliers } from "../events/overdrive";
import type { ProfileProgress } from "../progression/levels";
import type { CoreColor } from "../runtime/types";
import type { UpgradeEffects } from "../upgrades/effects";

export interface CollectorLanding {
  readonly laneIndex: number;
  readonly baseMultiplier: number;
  readonly appliedMultiplier: number;
  readonly polarityColor: CoreColor | null;
  readonly lensMatched: boolean;
  readonly lensBonusMultiplier: number;
}

export interface CollectorLaneView {
  readonly index: number;
  readonly label: string;
  readonly xMin: number;
  readonly xMax: number;
  readonly polarityColor: CoreColor | null;
  readonly lensBonusMultiplier: number;
}

const lanePolarityColors: readonly CoreColor[] = ["cyan", "pink", "lime", "pink", "cyan"];

export interface CollectorEventState {
  readonly overdriveKind: "normal" | "prestige" | null;
}

export function registerLanding(x: number, progress: ProfileProgress, effects: UpgradeEffects, coreColor?: CoreColor, events: CollectorEventState = { overdriveKind: null }): CollectorLanding {
  const matchedIndex = COLLECTOR_RULES.ranges.findIndex((range) => x >= range.minX && x <= range.maxX);
  const laneIndex = matchedIndex >= 0 ? matchedIndex : -1;
  const baseMultiplier = matchedIndex >= 0 ? laneMultipliers(progress, events.overdriveKind)[matchedIndex] : 1;
  const collectorUnlocked = progress.effectiveLevel >= 3;
  const polarityColor = matchedIndex >= 0 && collectorUnlocked ? lanePolarityColors[matchedIndex] : null;
  const lensMatched = Boolean(polarityColor && coreColor === polarityColor && effects.shellLensLaneBonusMultiplier > 1);
  const lensBonusMultiplier = lensMatched ? effects.shellLensLaneBonusMultiplier : 1;
  const laneMultiplier = collectorUnlocked && baseMultiplier > 1 ? baseMultiplier * effects.collectorAmpMultiplier : 1;
  return {
    laneIndex,
    baseMultiplier,
    appliedMultiplier: laneMultiplier * lensBonusMultiplier,
    polarityColor,
    lensMatched,
    lensBonusMultiplier
  };
}

export function collectorLaneViews(progress: ProfileProgress, effects: UpgradeEffects, events: CollectorEventState = { overdriveKind: null }): readonly CollectorLaneView[] {
  const unlocked = progress.effectiveLevel >= 3;
  const multipliers = laneMultipliers(progress, events.overdriveKind);
  return COLLECTOR_RULES.ranges.map((range, index) => ({
    index,
    label: unlocked ? `x${multipliers[index]}` : "x1",
    xMin: range.minX,
    xMax: range.maxX,
    polarityColor: unlocked ? lanePolarityColors[index] : null,
    lensBonusMultiplier: unlocked ? effects.shellLensLaneBonusMultiplier : 1
  }));
}

function laneMultipliers(progress: ProfileProgress, overdriveKind: CollectorEventState["overdriveKind"]): readonly number[] {
  return overdriveLaneMultipliers(progress, overdriveKind);
}
